import type { SampledFrame } from '../types';

function positiveModulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

export const DEFAULT_MANUAL_DRAG_FRAMES = 32;

export function normalizeManualDragFrames(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 2
    ? value
    : DEFAULT_MANUAL_DRAG_FRAMES;
}

/**
 * Snap a cyclic video time to one of `sampleCount` positions spread uniformly
 * over [0, duration). The media endpoint is deliberately excluded because it
 * is the same visual position as time zero in a complete 360-degree rotation.
 */
export function snapToUniformSample(
  time: number,
  duration: number,
  sampleCount: number,
): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  if (!Number.isInteger(sampleCount) || sampleCount < 2) return 0;

  const step = duration / sampleCount;
  const index = positiveModulo(Math.round(time / step), sampleCount);
  return index * step;
}

export function uniformSampleIndex(
  time: number,
  duration: number,
  sampleCount: number,
): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  if (!Number.isInteger(sampleCount) || sampleCount < 2) return 0;

  const step = duration / sampleCount;
  return positiveModulo(Math.round(time / step), sampleCount);
}

export function sampledFramesWithPaths(frames: SampledFrame[] | null | undefined): SampledFrame[] {
  return (frames ?? []).filter((frame): frame is SampledFrame & { Path: string } =>
    Boolean(frame.Path),
  );
}

/** Job-relative debug path (`sampled_frames/00.png`) from a storage Path. */
export function sampledFrameDebugPath(path: string): string {
  const marker = 'sampled_frames/';
  const index = path.replace(/\\/g, '/').lastIndexOf(marker);
  return index >= 0 ? path.slice(index) : path;
}

export { positiveModulo };
